import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import api, { BACKEND_URL } from '../services/api';
import { 
  User, 
  Mail, 
  Lock, 
  Camera, 
  Save, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';

const Profile = () => {
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    currentPassword: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        currentPassword: ''
      });
      if (user.photo_url) {
        setPreview(user.photo_url.startsWith('http') ? user.photo_url : `${BACKEND_URL}${user.photo_url}`);
      }
    }
  }, [user]);

  const MAX_FILE_MB = 5;
  const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError(`File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is ${MAX_FILE_MB} MB.`);
      e.target.value = '';
      return;
    }
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const data = new FormData();
      data.append('full_name', formData.full_name);
      data.append('email', formData.email);
      if (formData.password) {
        if (!formData.currentPassword) {
          setError('Current password is required to change password');
          setLoading(false);
          return;
        }
        data.append('password', formData.password);
        data.append('current_password', formData.currentPassword);
      }
      if (photo) data.append('photo', photo);
      else if (user.photo_url) data.append('photo_url', user.photo_url);

      const res = await api.put('/users/profile', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update context user
      const updatedUser = { ...user, ...res.data };
      setUser(updatedUser);
      
      setSuccess(true);
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '', currentPassword: '' }));
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (!err.response) {
        setError('Network error: Unable to connect to the backend server. Please ensure the backend is running.');
      } else {
        setError(err.response?.data?.error || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div>
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">Account Settings</h1>
        <p className="text-sm text-gray-500 font-semibold">Manage your professional profile and security preferences.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="card text-center space-y-6 flex flex-col items-center sticky top-8">
            <div className="relative group">
              <div className="w-32 h-32 rounded-[2.5rem] bg-indigo-50 overflow-hidden border-4 border-white shadow-xl flex items-center justify-center text-indigo-600 text-4xl font-black">
                {preview ? (
                  <img src={preview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  formData.full_name.charAt(0).toUpperCase()
                )}
              </div>
              <label className="absolute bottom-0 right-0 p-2.5 bg-indigo-600 text-white rounded-2xl shadow-lg cursor-pointer hover:bg-indigo-700 hover:scale-110 transition-all border-4 border-white" title="Max 5 MB · JPG, PNG">
                <Camera size={18} />
                <input type="file" className="hidden" onChange={handlePhotoChange} accept="image/jpeg, image/jpg, image/png" />
              </label>
            </div>
            
            <div>
              <h2 className="text-xl font-black text-gray-900">{formData.full_name || user?.username}</h2>
              <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mt-1">{user?.role}</p>
            </div>

            <div className="w-full pt-4 border-t border-gray-50 space-y-3">
               <div className="flex items-center justify-between text-xs font-bold px-4">
                 <span className="text-gray-400 uppercase tracking-widest">Username</span>
                 <span className="text-gray-900">{user?.username}</span>
               </div>
               {user?.email && (
                 <div className="flex items-center justify-between text-xs font-bold px-4 gap-2">
                   <span className="text-gray-400 uppercase tracking-widest shrink-0">Email</span>
                   <a href={`mailto:${user.email}`} className="text-indigo-600 hover:underline truncate text-right">{user.email}</a>
                 </div>
               )}
               <div className="flex items-center justify-between text-xs font-bold px-4">
                 <span className="text-gray-400 uppercase tracking-widest">Status</span>
                 <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px]">Verified</span>
               </div>
            </div>
          </div>
        </div>

        {/* Settings Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card !p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* General Info */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
                  <User size={20} className="text-indigo-600" />
                  <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm">Personal Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Full Display Name</label>
                    <input 
                      type="text" 
                      className="input-field" 
                      value={formData.full_name}
                      onChange={e => setFormData({...formData, full_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-3 text-gray-400" size={16} />
                      <input 
                        type="email" 
                        className="input-field pl-12" 
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Security */}
              <div className="space-y-6 pt-4">
                <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
                  <Lock size={20} className="text-rose-600" />
                  <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm">Security & Password</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Current Password</label>
                    <div className="relative">
                      <input 
                        type={showCurrentPassword ? "text" : "password"} 
                        className="input-field pr-10" 
                        placeholder="Current password"
                        value={formData.currentPassword}
                        onChange={e => setFormData({...formData, currentPassword: e.target.value})}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">New Password</label>
                    <div className="relative">
                      <input 
                        type={showNewPassword ? "text" : "password"} 
                        className="input-field pr-10" 
                        placeholder="Leave blank to keep current"
                        value={formData.password}
                        onChange={e => setFormData({...formData, password: e.target.value})}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Confirm New Password</label>
                    <div className="relative">
                      <input 
                        type={showConfirmPassword ? "text" : "password"} 
                        className="input-field pr-10" 
                        placeholder="Repeat new password"
                        value={formData.confirmPassword}
                        onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feedback Messages */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-rose-50 text-rose-700 rounded-2xl text-sm font-bold animate-in slide-in-from-top-2">
                  <AlertCircle size={20} />
                  {error}
                </div>
              )}

              {success && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 text-emerald-700 rounded-2xl text-sm font-bold animate-in slide-in-from-top-2">
                  <CheckCircle2 size={20} />
                  Profile updated successfully!
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end pt-4">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary !px-10 !py-4 shadow-xl shadow-indigo-100 disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Save size={18} />
                  )}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
